import { ContactFormData } from '@upright/common/activities/contact-forms/facade/contact-form';
import { setModelVersionAndRelease as _setModelVersionAndRelease } from '@upright/ui/untidy/redux/actions/actions';
import { createContext, useContext, useMemo, useState } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { GetPortfolioSearchSuggestionsResponse } from '../../../../../common/iam/access/api-types';
import { DEFAULT_RELATIVE_NET_SCORE_FORMAT, NetScoreFormat } from '../../../../../common/model/output/scores/types';
import { usePreFetchMyFollowedEntities } from '../../../../api/iam/followed-entities/hooks';
import { usePreFetchMyOrganizations } from '../../../../api/iam/organizations/hooks';
import { getPortfolioSearchSuggestions } from '../../../../api/model/output/scores/portfolios';
import {
  getDefaultVersionAndReleaseIfExists,
  getVersionAndReleaseIfExists,
  loggedInUser,
} from '../../../../untidy/redux/selectors';
import { useLoadAllImpactLabels } from '../../context/use-load-all-impact-labels';
import { useLoadDefaultVersionAndRelease } from '../../context/use-load-default-version-and-release';
import { useLoadGlobalFigures } from './use-load-global-figures';
import { useLoadImpactSizeFactors } from './use-load-impact-score-factors';
import { useLoadReferenceFigures } from './use-load-reference-figures';

interface MainContext {
  contactModal: [
    open: boolean,
    setOpen: (o: boolean) => void,
    initialValues: Partial<ContactFormData> | undefined,
    setInitialValues: (newValues: Partial<ContactFormData> | undefined) => void,
  ];

  netScoreFormat: NetScoreFormat;
  setNetScoreFormat: (mode: NetScoreFormat) => void;

  getPortfolioSearchSuggestionsQuery: UseQueryResult<GetPortfolioSearchSuggestionsResponse, Error>;

  resetModelVersionAndRelease: () => void;
  setModelVersionAndRelease: (version: string, release: string | null) => void;
  modelVersionAndRelease: VersionAndRelease | undefined;
}
interface VersionAndRelease {
  version: string;
  release: string | null;
}

const ctx = createContext<MainContext>({} as MainContext);
export const MainContextProvider = ctx.Provider;

export function useMainContextBuilder(): MainContext {
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalInitialValues, setContactModalInitialValues] = useState<Partial<ContactFormData>>();
  const [netScoreFormat, setNetScoreFormat] = useState<NetScoreFormat>(DEFAULT_RELATIVE_NET_SCORE_FORMAT);

  const user = useSelector(loggedInUser);

  const versioning = useVersioning();

  // TODO ML: Move to its distinct hook away from main context
  const getPortfolioSearchSuggestionsQuery = useQuery({
    queryKey: ['portfolioSearchSuggestions', { id: user?.id, username: user?.username }],
    queryFn: getPortfolioSearchSuggestions,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!user,
  });

  usePreFetchMyFollowedEntities();
  usePreFetchMyOrganizations();

  // below hooks trigger loading of
  // different data to global redux state
  useLoadReferenceFigures();
  useLoadGlobalFigures();
  useLoadImpactSizeFactors();
  // PLT-4774: the slim product hierarchy is no longer loaded at boot.
  // Each view that needs it reads from `useProductsByCode()` (React Query
  // backed). This keeps boot payload small for customer-facing pages that
  // never look at the taxonomy graph (DMA dashboard, company overview,
  // score views).
  useLoadAllImpactLabels();
  useLoadDefaultVersionAndRelease();

  return {
    netScoreFormat,
    setNetScoreFormat,
    getPortfolioSearchSuggestionsQuery,
    contactModal: [contactModalOpen, setContactModalOpen, contactModalInitialValues, setContactModalInitialValues],
    ...versioning,
  };
}

export function useVersioning() {
  const dispatch = useDispatch();
  const { version, release } = useSelector(getVersionAndReleaseIfExists);
  const defaults = useSelector(getDefaultVersionAndReleaseIfExists);

  const resetModelVersionAndRelease = useMemo(() => {
    return () => {
      if (defaults) {
        const { defaultVersion, defaultRelease } = defaults;
        dispatch(_setModelVersionAndRelease(defaultVersion, defaultRelease));
      }
    };
  }, [dispatch, defaults]);

  const setModelVersionAndRelease = useMemo(() => {
    return (newVersion: string, newRelease: string | null) => {
      dispatch(_setModelVersionAndRelease(newVersion, newRelease));
    };
  }, [dispatch]);

  const modelVersionAndRelease = version !== undefined && release !== undefined ? { version, release } : undefined;

  return {
    resetModelVersionAndRelease,
    setModelVersionAndRelease,
    modelVersionAndRelease,
  };
}

export function useMainContext() {
  return useContext(ctx);
}
